import {
  fsrs,
  generatorParameters,
  Rating,
  State,
  type Card,
  type Grade,
} from "ts-fsrs";

/**
 * FSRS scheduler wrapper (BW18 PR-J, refactored for HOTFIX-BW19.4 PR-A
 * Bug 8 multilingual schema).
 *
 * Each translation row holds its own FSRS state in jsonb, plus separate
 * due_at / last_reviewed_at / review_count columns for indexing. The
 * scheduler unpacks the jsonb, runs ts-fsrs, and emits a patch the
 * caller can write back.
 */

const params = generatorParameters({
  enable_fuzz: true,
  enable_short_term: true,
});
const scheduler = fsrs(params);

export type FsrsStateJson = {
  stability?: number;
  difficulty?: number;
  elapsed_days?: number;
  scheduled_days?: number;
  reps?: number;
  lapses?: number;
  state?: "new" | "learning" | "review" | "relearning";
};

export type TranslationRow = {
  id: string;
  due_at: string;
  last_reviewed_at: string | null;
  review_count: number;
  fsrs_state: FsrsStateJson | null;
};

export type SchedulerRating = "again" | "hard" | "good" | "easy";

const STATE_TO_DB: Record<State, NonNullable<FsrsStateJson["state"]>> = {
  [State.New]: "new",
  [State.Learning]: "learning",
  [State.Review]: "review",
  [State.Relearning]: "relearning",
};

const DB_TO_STATE: Record<NonNullable<FsrsStateJson["state"]>, State> = {
  new: State.New,
  learning: State.Learning,
  review: State.Review,
  relearning: State.Relearning,
};

const RATING_MAP: Record<SchedulerRating, Grade> = {
  again: Rating.Again,
  hard: Rating.Hard,
  good: Rating.Good,
  easy: Rating.Easy,
};

export const RATING_TO_INT: Record<SchedulerRating, 1 | 2 | 3 | 4> = {
  again: 1,
  hard: 2,
  good: 3,
  easy: 4,
};

function unpack(state: FsrsStateJson | null): Required<FsrsStateJson> {
  return {
    stability: state?.stability ?? 0,
    difficulty: state?.difficulty ?? 0,
    elapsed_days: state?.elapsed_days ?? 0,
    scheduled_days: state?.scheduled_days ?? 0,
    reps: state?.reps ?? 0,
    lapses: state?.lapses ?? 0,
    state: state?.state ?? "new",
  };
}

function rowToCard(row: TranslationRow, now: Date): Card {
  const s = unpack(row.fsrs_state);
  return {
    due: new Date(row.due_at),
    stability: s.stability,
    difficulty: s.difficulty,
    elapsed_days: s.elapsed_days,
    scheduled_days: s.scheduled_days,
    learning_steps: 0,
    reps: s.reps,
    lapses: s.lapses,
    state: DB_TO_STATE[s.state],
    last_review: row.last_reviewed_at ? new Date(row.last_reviewed_at) : now,
  };
}

export type TranslationPatch = {
  due_at: string;
  last_reviewed_at: string;
  review_count: number;
  fsrs_state: FsrsStateJson;
};

/**
 * Apply a rating to a translation row and return the persistent patch.
 * Caller writes the patch to vocab_translation and inserts a vocab_review row.
 */
export function applyRating(
  row: TranslationRow,
  rating: SchedulerRating,
  now: Date = new Date(),
): TranslationPatch {
  const card = rowToCard(row, now);
  const result = scheduler.next(card, now, RATING_MAP[rating]);
  const next = result.card;
  return {
    due_at: next.due.toISOString(),
    last_reviewed_at: now.toISOString(),
    review_count: row.review_count + 1,
    fsrs_state: {
      stability: next.stability,
      difficulty: next.difficulty,
      elapsed_days: next.elapsed_days,
      scheduled_days: next.scheduled_days,
      reps: next.reps,
      lapses: next.lapses,
      state: STATE_TO_DB[next.state],
    },
  };
}

/**
 * Pure helper used by the stats page to label a card's bucket without
 * spinning the scheduler back up.
 */
export function describeState(state: FsrsStateJson["state"] | null | undefined): string {
  switch (state ?? "new") {
    case "new":
      return "New";
    case "learning":
      return "Learning";
    case "review":
      return "Review";
    case "relearning":
      return "Relearning";
  }
}
